/**
 * WorldsPage - Browse available 360° virtual tour worlds
 *
 * Design: Dark background, pink accent buttons, card grid
 * Access gate: First-time users see "Apply for Access" with approval animation
 * Analytics: Tracks page views and world entries
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import type { World } from '@/config/worldsConfig'
import { BRAND_COLORS } from '@/config/brandConfig'
import { trackWorldsEvent } from '@/services/worldsAnalytics'
import { fetchWorlds } from '@/services/worldsService'

const ACCESS_KEY = 'worlds_access_granted'

// Access Gate — "Apply for Access" with approval animation
function AccessGate({ onGranted }: { onGranted: () => void }) {
  const [phase, setPhase] = useState<'apply' | 'approving' | 'approved'>('apply')
  const [progress, setProgress] = useState(0)

  const handleApply = () => {
    setPhase('approving')
    trackWorldsEvent('worlds_access_applied')

    // Fake approval progress over ~15 seconds
    let p = 0
    const interval = setInterval(() => {
      p += Math.random() * 8 + 2
      if (p >= 100) {
        p = 100
        clearInterval(interval)
        setPhase('approved')
        trackWorldsEvent('worlds_access_granted')
        setTimeout(() => {
          localStorage.setItem(ACCESS_KEY, 'true')
          onGranted()
        }, 1500)
      }
      setProgress(Math.min(p, 100))
    }, 800)
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {phase === 'apply' && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full border-2 border-pink-500/30 flex items-center justify-center">
              <svg className="w-10 h-10 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-white text-2xl font-semibold mb-3">Exclusive Access</h1>
            <p className="text-gray-400 text-sm mb-8 leading-relaxed">
              Worlds are immersive 360° experiences. Apply below to unlock access to virtual tours of private spaces.
            </p>
            <button
              onClick={handleApply}
              className="w-full py-3.5 rounded-xl font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.98]"
              style={{ backgroundColor: BRAND_COLORS.primary, color: 'white' }}
            >
              Apply for Access
            </button>
          </div>
        )}

        {phase === 'approving' && (
          <div className="animate-fade-in">
            <div className="w-16 h-16 mx-auto mb-6 relative">
              <div className="absolute inset-0 rounded-full border-2 border-gray-800" />
              <div className="absolute inset-0 rounded-full border-2 border-pink-500 border-t-transparent animate-spin" />
            </div>
            <h2 className="text-white text-xl font-semibold mb-2">Reviewing your access...</h2>
            <p className="text-gray-500 text-sm mb-6">This usually takes a moment</p>
            <div className="w-full bg-gray-800 rounded-full h-1.5 mb-2">
              <div
                className="h-1.5 rounded-full transition-all duration-700 ease-out"
                style={{ width: `${progress}%`, backgroundColor: BRAND_COLORS.primary }}
              />
            </div>
            <p className="text-gray-600 text-xs">{Math.round(progress)}%</p>
          </div>
        )}

        {phase === 'approved' && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/10 flex items-center justify-center">
              <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-white text-xl font-semibold mb-2">Access Granted</h2>
            <p className="text-gray-400 text-sm">Welcome in.</p>
          </div>
        )}
      </div>
    </div>
  )
}

// World Card
const LIKES_KEY = 'worlds_likes'

function getStoredLikes(): string[] {
  try { return JSON.parse(localStorage.getItem(LIKES_KEY) || '[]') } catch { return [] }
}

function toggleStoredLike(worldId: string): string[] {
  const likes = getStoredLikes()
  const idx = likes.indexOf(worldId)
  if (idx >= 0) likes.splice(idx, 1)
  else likes.push(worldId)
  localStorage.setItem(LIKES_KEY, JSON.stringify(likes))
  return [...likes]
}

function WorldCard({ world, onEnter }: { world: World; onEnter: (id: string) => void }) {
  const [likes, setLikes] = useState(() => getStoredLikes())
  const isLiked = likes.includes(world.id)

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation()
    const updated = toggleStoredLike(world.id)
    setLikes(updated)
    trackWorldsEvent(isLiked ? 'world_unliked' : 'world_liked', { world_id: world.id })
  }

  return (
    <div className="rounded-xl border border-gray-800 overflow-hidden bg-gray-900/50 group transition-all hover:border-gray-700">
      {/* Thumbnail */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={world.thumbnail}
          alt={world.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {world.isNew && (
            <span
              className="px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider"
              style={{ backgroundColor: BRAND_COLORS.primary, color: 'white' }}
            >
              New
            </span>
          )}
          {world.isLocked && (
            <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider bg-gray-800/80 text-gray-300 backdrop-blur-sm flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </span>
          )}
        </div>
        {/* Heart - functional like toggle */}
        <button
          onClick={handleLike}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full backdrop-blur-sm flex items-center justify-center transition-all active:scale-90 ${
            isLiked ? 'bg-pink-500/30 text-pink-400' : 'bg-black/30 text-white/70 hover:text-pink-400'
          }`}
        >
          <svg className="w-4 h-4" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-white font-semibold text-lg mb-1">{world.title}</h3>
        <div className="flex items-center gap-1.5 text-gray-400 text-sm mb-2">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {world.location}
        </div>
        <p className="text-gray-500 text-sm mb-4 line-clamp-2">{world.description}</p>

        {world.isLocked ? (
          <button className="w-full py-2.5 rounded-lg border border-gray-700 text-gray-400 text-sm font-medium flex items-center justify-center gap-2 cursor-not-allowed">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Locked
          </button>
        ) : (
          <button
            onClick={() => onEnter(world.id)}
            className="w-full py-2.5 rounded-lg font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.98]"
            style={{ backgroundColor: BRAND_COLORS.primary, color: 'white' }}
          >
            Enter World
          </button>
        )}
      </div>
    </div>
  )
}

export function WorldsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [hasAccess, setHasAccess] = useState(() => localStorage.getItem(ACCESS_KEY) === 'true')
  const [worlds, setWorlds] = useState<World[]>([])
  const [loadingWorlds, setLoadingWorlds] = useState(true)

  useEffect(() => {
    if (hasAccess) {
      trackWorldsEvent('worlds_page_viewed')
      fetchWorlds().then((data) => { setWorlds(data); setLoadingWorlds(false) })
    }
  }, [hasAccess])

  const handleEnterWorld = useCallback((worldId: string) => {
    trackWorldsEvent('world_entered', { world_id: worldId })
    navigate(`/worlds/${worldId}`)
  }, [navigate])

  if (!hasAccess) {
    return <AccessGate onGranted={() => setHasAccess(true)} />
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-black/80 backdrop-blur-lg border-b border-gray-800/50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/welcome')}
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              &larr;
            </button>
            <h1
              className="text-sm font-semibold uppercase tracking-[0.2em]"
              style={{ color: BRAND_COLORS.primary }}
            >
              Available Worlds
              <span className="text-[9px] font-medium tracking-widest uppercase px-1.5 py-0.5 rounded ml-2" style={{ backgroundColor: 'rgba(236,72,153,0.15)', color: BRAND_COLORS.primary, fontFamily: 'monospace' }}>beta</span>
            </h1>
          </div>
          {user?.email && (
            <span className="text-gray-600 text-xs hidden sm:block">{user.email}</span>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {loadingWorlds && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: BRAND_COLORS.primary }} />
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {worlds.map((world) => (
            <WorldCard key={world.id} world={world} onEnter={handleEnterWorld} />
          ))}
        </div>

        {/* Coming soon banner with stacked card preview */}
        <div className="mt-8 rounded-xl border border-gray-800 bg-gray-900/30 p-5 flex items-center gap-6 overflow-hidden">
          {/* Stacked card previews */}
          <div className="relative shrink-0 w-28 h-20 hidden sm:block">
            {['/tour/city-penthouse.jpg', '/tour/skyline-suite.jpg'].map((src, i) => (
              <div
                key={src}
                className="absolute rounded-lg overflow-hidden border border-gray-700 shadow-lg"
                style={{
                  width: 80,
                  height: 56,
                  left: i * 18,
                  top: i * 4,
                  zIndex: 2 - i,
                  transform: `rotate(${(i - 0.5) * 6}deg)`,
                }}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/30" />
              </div>
            ))}
          </div>
          <div className="flex-1">
            <p
              className="text-sm font-semibold uppercase tracking-wider mb-1"
              style={{ color: BRAND_COLORS.primary }}
            >
              More Worlds Coming Soon
            </p>
            <p className="text-gray-500 text-sm">
              Exclusive destinations, private moments, and new experiences are on the way.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
